<!--
  Sidebar Component
  Collapsible navigation: expanded (logo + text) ↔ collapsed (icon-only with tooltip).
  Uses Lucide icons. Active state highlighted with accent color.
-->
<script lang="ts">
  import { Link, useLocation } from 'svelte-routing';
  import { _ } from 'svelte-i18n';
  import LayoutDashboard from 'lucide-svelte/icons/layout-dashboard';
  import Search from 'lucide-svelte/icons/search';
  import BarChart3 from 'lucide-svelte/icons/bar-chart-3';
  import Settings from 'lucide-svelte/icons/settings';
  import PanelLeftClose from 'lucide-svelte/icons/panel-left-close';
  import PanelLeftOpen from 'lucide-svelte/icons/panel-left-open';
  import Inbox from 'lucide-svelte/icons/inbox';
  import CopyCheck from 'lucide-svelte/icons/copy-check';
  import Trash2 from 'lucide-svelte/icons/trash-2';
  import type { ComponentType } from 'svelte';
  import Tooltip from '../ui/Tooltip.svelte';

  export let collapsed = false;

  const location = useLocation();

  interface NavItem {
    icon: ComponentType;
    labelKey: string;
    href: string;
    match?: RegExp;
  }

  const navItems: NavItem[] = [
    { icon: LayoutDashboard, labelKey: 'nav.dashboard', href: '/', match: /^\/$|^\/upload/ },
    { icon: Inbox, labelKey: 'nav.inbox', href: '/inbox', match: /^\/inbox/ },
    { icon: Search, labelKey: 'nav.documents', href: '/browse', match: /^\/(browse|document)/ },
    { icon: BarChart3, labelKey: 'nav.reports', href: '/reports', match: /^\/reports/ },
    { icon: CopyCheck, labelKey: 'nav.duplicates', href: '/duplicates', match: /^\/duplicates/ },
    { icon: Trash2, labelKey: 'nav.bin', href: '/bin', match: /^\/bin/ },
    { icon: Settings, labelKey: 'nav.settings', href: '/settings', match: /^\/settings/ },
  ];

  function isActive(item: NavItem, pathname: string): boolean {
    if (item.match) return item.match.test(pathname);
    return pathname === item.href;
  }

  function toggle() {
    collapsed = !collapsed;
  }
</script>

<aside
  class="h-screen sticky top-0 flex flex-col bg-bg-alt border-r border-border transition-all duration-200
    {collapsed ? 'w-16' : 'w-56'}"
>
  <!-- Logo area -->
  <div class="h-14 flex items-center px-4 border-b border-border shrink-0">
    {#if !collapsed}
      <Link to="/" class="flex items-center gap-2 text-text no-underline">
        <img src="/logo/logo-icon.svg" alt="Paperless" class="h-7" />
        <span class="text-2xl text-text">Paperless</span>
      </Link>
    {:else}
      <Link to="/" class="flex items-center justify-center w-full text-text no-underline">
        <img src="/logo/logo-icon.svg" alt="Paperless" class="h-7" />
      </Link>
    {/if}
  </div>

  <!-- Navigation -->
  <nav class="flex-1 py-3 px-2 flex flex-col gap-0.5">
    {#each navItems as item (item.href)}
      {@const active = isActive(item, $location.pathname)}
      {#if collapsed}
        <Tooltip text={$_(item.labelKey)} position="right">
          <Link
            to={item.href}
            class="flex items-center justify-center w-full h-10 rounded transition-colors
              {active
              ? 'bg-accent-subtle text-accent'
              : 'text-text-secondary hover:bg-surface hover:text-text'}"
          >
            <svelte:component this={item.icon} class="w-5 h-5" strokeWidth={1.5} />
          </Link>
        </Tooltip>
      {:else}
        <Link
          to={item.href}
          class="flex items-center gap-3 px-3 h-10 rounded transition-colors text-sm font-medium
            {active
            ? 'bg-accent-subtle text-accent'
            : 'text-text-secondary hover:bg-surface hover:text-text'}"
        >
          <svelte:component this={item.icon} class="w-5 h-5 shrink-0" strokeWidth={1.5} />
          <span class="whitespace-nowrap">{$_(item.labelKey)}</span>
        </Link>
      {/if}
    {/each}
  </nav>

  <!-- Collapse toggle -->
  <div class="px-2 py-3 border-t border-border">
    {#if collapsed}
      <Tooltip text={$_('nav.expand_sidebar')} position="right">
        <button
          class="flex items-center justify-center w-full h-10 rounded text-text-secondary
            hover:bg-surface hover:text-text transition-colors bg-transparent border-none cursor-pointer"
          on:click={toggle}
          aria-label={$_('nav.expand_sidebar')}
        >
          <PanelLeftOpen class="w-5 h-5" strokeWidth={1.5} />
        </button>
      </Tooltip>
    {:else}
      <button
        class="flex items-center gap-3 px-3 w-full h-10 rounded text-sm text-text-secondary
          hover:bg-surface hover:text-text transition-colors bg-transparent border-none cursor-pointer"
        on:click={toggle}
        aria-label={$_('nav.collapse_sidebar')}
      >
        <PanelLeftClose class="w-5 h-5 shrink-0" strokeWidth={1.5} />
        <span class="whitespace-nowrap">{$_('nav.collapse_sidebar')}</span>
      </button>
    {/if}
  </div>
</aside>
